import { useState, useEffect, createContext, useContext, Component, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { WhatsAppBubble } from "@/components/WhatsAppBubble";
import { toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { Loader2 } from "lucide-react";

// Loading Fallback Component
const PageLoader = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37] mx-auto mb-4" />
      <p className="text-zinc-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-zinc-400 mb-6">An error occurred while loading this page.</p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              className="px-6 py-3 bg-[#D4AF37] text-black font-medium hover:bg-[#C4A030] transition-colors"
            >
              Return to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ============ LAZY LOADED PAGES (Code Splitting) ============
// Critical pages loaded immediately
import TempleHome from "@/pages/TempleHome";

// B2B Pages - Lazy loaded (not needed for most visitors)
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage"));
const QuoteSignup = lazy(() => import("@/pages/QuoteSignup"));
const MySavedItems = lazy(() => import("@/pages/MySavedItems"));
const MyArtistProfile = lazy(() => import("@/pages/MyArtistProfile"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const QuoteBuilder = lazy(() => import("@/pages/QuoteBuilder"));
const ManagerDashboard = lazy(() => import("@/pages/ManagerDashboard"));
const MasterDashboard = lazy(() => import("@/pages/MasterDashboard"));
const ManagerQuotes = lazy(() => import("@/pages/ManagerQuotes"));
const MyQuotes = lazy(() => import("@/pages/MyQuotes"));
const QuotePreview = lazy(() => import("@/pages/QuotePreview"));
const QuoteEdit = lazy(() => import("@/pages/QuoteEdit"));
const AnalyticsDashboard = lazy(() => import("@/pages/AnalyticsDashboard"));
const Brochure = lazy(() => import("@/pages/Brochure"));
const MyProfile = lazy(() => import("@/pages/MyProfile"));
const LDCBrochure = lazy(() => import("@/pages/LDCBrochure"));
const CustomShop = lazy(() => import("@/pages/CustomShop"));
const ContactDetailPage = lazy(() => import("@/pages/ContactDetailPage"));
const SocialMedia = lazy(() => import("@/pages/SocialMedia"));

// B2C Pages - Lazy loaded (loaded on demand)
const PublicHome = lazy(() => import("@/pages/PublicHome"));
const PublicShop = lazy(() => import("@/pages/PublicShop"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const CheckoutSuccess = lazy(() => import("@/pages/CheckoutSuccess"));
const CustomerAuth = lazy(() => import("@/pages/CustomerAuth"));
const CustomerAccount = lazy(() => import("@/pages/CustomerAccount"));
const LDCPublicShop = lazy(() => import("@/pages/LDCPublicShop"));
const LDCCustomBuilder = lazy(() => import("@/pages/LDCCustomBuilder"));
const MerchShop = lazy(() => import("@/pages/MerchShop"));
const LiveShop = lazy(() => import("@/pages/LiveShop"));
const SharedWishlist = lazy(() => import("@/pages/SharedWishlist"));
const OrderTracking = lazy(() => import("@/pages/OrderTracking"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const StockistDeck = lazy(() => import("@/pages/StockistDeck"));
const FindAStockist = lazy(() => import("@/pages/FindAStockist"));

// Content Pages - Lazy loaded
const VideosPage = lazy(() => import("@/pages/VideosPage"));

// Artists Page - lazy imports at module scope for proper Suspense caching
const ArtistsPage = lazy(() => import("@/pages/ArtistsPage").then(module => ({ default: module.default })));
const ArtistDetail = lazy(() => import("@/pages/ArtistsPage").then(module => ({ default: module.ArtistDetail })));

// Chat Widget for B2B users
import ChatWidget from "@/components/chat/ChatWidget";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Dynamically determine API URL - use env var if set, otherwise use current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
export const API = `${BACKEND_URL}/api`;

// Configure axios to always send cookies (for httpOnly auth and CSRF)
axios.defaults.withCredentials = true;

// CSRF Token Management
let csrfToken = null;
let csrfTokenPromise = null;
let isRefreshingCsrf = false;

// Force refresh CSRF token (clears cache first, forces new token from server)
const refreshCsrfToken = async () => {
  csrfToken = null;
  csrfTokenPromise = null;
  isRefreshingCsrf = true;
  try {
    const response = await axios.get(`${API}/csrf/token?force_new=true`, {
      headers: { 'X-Skip-CSRF': 'true' }
    });
    csrfToken = response.data.csrf_token;
    return csrfToken;
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
    return null;
  } finally {
    isRefreshingCsrf = false;
  }
};

// Fetch CSRF token from backend
const fetchCsrfToken = async () => {
  // If already fetching, return existing promise
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }
  
  csrfTokenPromise = (async () => {
    try {
      const response = await axios.get(`${API}/csrf/token`, {
        headers: { 'X-Skip-CSRF': 'true' }
      });
      csrfToken = response.data.csrf_token;
      return csrfToken;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    } finally {
      // Reset promise so future calls can retry
      csrfTokenPromise = null;
    }
  })();
  
  return csrfTokenPromise;
};

// Initialize CSRF token on app load
fetchCsrfToken();

// Request interceptor - adds Authorization header and CSRF token
let requestInterceptorId = null;
const setupRequestInterceptor = () => {
  // Remove existing interceptor to prevent stacking
  if (requestInterceptorId !== null) {
    axios.interceptors.request.eject(requestInterceptorId);
  }
  requestInterceptorId = axios.interceptors.request.use(async (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add CSRF token for state-changing requests (skip for CSRF fetch itself)
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method) && !config.headers['X-Skip-CSRF']) {
      // Ensure we have a CSRF token
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    
    // Remove internal header
    delete config.headers['X-Skip-CSRF'];
    
    return config;
  });
};
setupRequestInterceptor();

// CSRF Response interceptor - auto-refresh token and retry on 403 CSRF errors
let csrfResponseInterceptorId = null;
const setupCsrfResponseInterceptor = () => {
  if (csrfResponseInterceptorId !== null) {
    axios.interceptors.response.eject(csrfResponseInterceptorId);
  }
  csrfResponseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // Skip if this is the CSRF token fetch itself (prevent loops)
      const isCsrfTokenRequest = originalRequest?.url?.includes('/csrf/token');
      
      // Check if this is a CSRF token error (403 with CSRF message)
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.includes('CSRF') &&
        !originalRequest._csrfRetried &&  // Prevent infinite retry loops
        !isCsrfTokenRequest &&  // Don't retry the token fetch itself
        !isRefreshingCsrf  // Don't retry while already refreshing
      ) {
        originalRequest._csrfRetried = true;
        
        console.log('CSRF token invalid, refreshing and retrying...');
        
        // Refresh the CSRF token (forces new token)
        const newToken = await refreshCsrfToken();
        
        if (newToken) {
          // Update the request header with new token
          originalRequest.headers['X-CSRF-Token'] = newToken;
          // Retry the original request
          return axios(originalRequest);
        }
      }
      
      return Promise.reject(error);
    }
  );
};
setupCsrfResponseInterceptor();

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Response interceptor - handles auth errors
let responseInterceptorId = null;
const setupAxiosInterceptors = (logout) => {
  // Remove existing interceptor to prevent stacking on re-renders
  if (responseInterceptorId !== null) {
    axios.interceptors.response.eject(responseInterceptorId);
  }
  responseInterceptorId = axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Only logout on 401 (unauthorized) - not 403 (forbidden)
      if (error.response?.status === 401) {
        const errorDetail = error.response?.data?.detail || '';
        // Don't show error toast for initial auth check (expected to fail if not logged in)
        if (!error.config?.url?.includes('/auth/me')) {
          if (errorDetail.includes('not activated')) {
            toast.error('Your account is not activated yet. Please contact a manager.');
          } else if (errorDetail.includes('expired')) {
            toast.error('Your session has expired. Please log in again.');
          }
        }
        logout();
      }
      // 403 errors should show the error message but not logout
      // CSRF errors are handled by the CSRF interceptor with auto-retry
      // Only show error if it's NOT a CSRF error (CSRF interceptor handles those)
      if (error.response?.status === 403 && error.response?.data?.detail) {
        const isCsrfError = error.response?.data?.detail?.includes('CSRF');
        // Never show CSRF errors here - let CSRF interceptor handle them
        // If CSRF retry fails, the error will come back with _csrfRetried=true
        if (!isCsrfError) {
          toast.error(error.response.data.detail);
        } else if (error.config?._csrfRetried) {
          // CSRF retry already happened and failed
          toast.error(error.response.data.detail);
        }
        // If it's a CSRF error without _csrfRetried, CSRF interceptor will handle it
      }
      return Promise.reject(error);
    }
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireManager = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  
  if (requireManager && !user.is_manager) {
    return <Navigate to="/quotes/new" replace />;
  }
  
  return children;
};

// Persistent banner shown while a manager is acting as a customer
const ImpersonationBanner = () => {
  const { impersonation, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  if (!impersonation) return null;
  const handleExit = async () => {
    await stopImpersonation();
    navigate("/manager");
  };
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium shadow-lg"
      data-testid="impersonation-banner"
    >
      <span>
        🔑 Skeleton key active — you are viewing <strong>{impersonation.target?.email}</strong>'s account
        {impersonation.actor?.email ? ` (as ${impersonation.actor.email})` : ''}
      </span>
      <button
        onClick={handleExit}
        className="ml-2 bg-black text-amber-400 hover:bg-zinc-800 px-3 py-1 rounded-sm font-semibold transition-colors"
        data-testid="exit-impersonation-button"
      >
        Exit
      </button>
    </div>
  );
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState(null);

  const logout = async () => {
    try {
      // Call backend to clear the httpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors during logout
    }
    // Clear localStorage token
    localStorage.removeItem('auth_token');
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonation_ctx');
    setImpersonation(null);
    setUser(null);
  };

  useEffect(() => {
    // Ensure request interceptor is set up before checking auth
    setupRequestInterceptor();
    setupAxiosInterceptors(logout);
    // Re-register CSRF interceptor AFTER auth interceptor
    // so CSRF interceptor runs first (interceptors run in reverse order)
    setupCsrfResponseInterceptor();
    
    // Check if user is authenticated by calling /auth/me
    // Will try both httpOnly cookie and Authorization header (from localStorage)
    axios.get(`${API}/auth/me`)
      .then((res) => {
        setUser(res.data);
        if (res.data._impersonated) {
          setImpersonation({ target: res.data, actor: res.data._impersonated_by });
        }
      })
      .catch((err) => {
        // If auth fails, clear any stored token
        // Only clear if it's actually an auth error (401), not network error
        if (err.response?.status === 401) {
          localStorage.removeItem('auth_token');
        }
        setUser(null);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    // The login endpoint will set the httpOnly cookie automatically
    // We also store the token in localStorage as fallback for CORS issues
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData, token } = res.data;
    
    // Store token in localStorage as backup for when cookies don't work
    if (token) {
      localStorage.setItem('auth_token', token);
      // Re-setup request interceptor to ensure token is attached
      setupRequestInterceptor();
    }
    
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, company = "", phone = "") => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, company, phone });
    return res.data;
  };

  // Set session directly from a token + user (used by quote onboarding sign-up)
  const loginWithToken = (token, userData) => {
    if (token) {
      localStorage.setItem('auth_token', token);
      setupRequestInterceptor();
    }
    setUser(userData);
    return userData;
  };

  // Skeleton key: start acting as a customer account
  const startImpersonation = async (userId) => {
    const original = localStorage.getItem('auth_token');
    const res = await axios.post(`${API}/users/${userId}/impersonate`);
    if (original) localStorage.setItem('impersonator_token', original);
    localStorage.setItem('auth_token', res.data.token);
    setupRequestInterceptor();
    const ctx = { target: res.data.user, actor: res.data.user.impersonated_by };
    localStorage.setItem('impersonation_ctx', JSON.stringify(ctx));
    setImpersonation(ctx);
    setUser(res.data.user);
    return res.data.user;
  };

  // Return to the manager's own session without re-login
  const stopImpersonation = async () => {
    const original = localStorage.getItem('impersonator_token');
    localStorage.removeItem('impersonation_ctx');
    setImpersonation(null);
    if (!original) { await logout(); return; }
    localStorage.setItem('auth_token', original);
    localStorage.removeItem('impersonator_token');
    setupRequestInterceptor();
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      await logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, register, loginWithToken, startImpersonation, stopImpersonation, impersonation, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <ErrorBoundary>
            <Toaster position="top-right" richColors />
            <ImpersonationBanner />
            <WhatsAppBubble />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes - Temple of Sound */}
                <Route path="/" element={<TempleHome />} />
                <Route path="/shop" element={<PublicShop />} />
                <Route path="/cymbals" element={<PublicShop />} />
                <Route path="/drumsticks" element={<LDCPublicShop />} />
                <Route path="/custom-drumsticks" element={<LDCCustomBuilder />} />
                <Route path="/artists" element={<ArtistsPage />} />
                <Route path="/artists/:artistSlug" element={<ArtistDetail />} />
                <Route path="/live-shop" element={<LiveShop />} />
                <Route path="/videos" element={<VideosPage />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/accept-invite" element={<AcceptInvitePage />} />
              <Route path="/quote-signup" element={<QuoteSignup />} />
          
          {/* Protected B2B Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedRoute>
                <QuoteBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <MyQuotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-saved-items"
            element={
              <ProtectedRoute>
                <MySavedItems />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artist-profile"
            element={
              <ProtectedRoute>
                <MyArtistProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id"
            element={
              <ProtectedRoute>
                <QuotePreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:id/edit"
            element={
              <ProtectedRoute>
                <QuoteEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brochure"
            element={
              <ProtectedRoute>
                <Brochure />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ldc-brochure"
            element={
              <ProtectedRoute>
                <LDCBrochure />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-shop"
            element={
              <ProtectedRoute>
                <CustomShop />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager"
            element={
              <ProtectedRoute requireManager>
                <MasterDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/legacy"
            element={
              <ProtectedRoute requireManager>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/quotes"
            element={
              <ProtectedRoute requireManager>
                <ManagerQuotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/contact/:userId"
            element={
              <ProtectedRoute requireManager>
                <ContactDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/social"
            element={
              <ProtectedRoute requireManager>
                <SocialMedia />
              </ProtectedRoute>
            }
          />
          
          {/* B2C Customer Routes */}
          <Route path="/customer/auth" element={<CustomerAuth />} />
          <Route path="/account" element={<CustomerAccount />} />
          <Route path="/customer/account" element={<CustomerAccount />} />
          <Route path="/checkout" element={<Checkout />} />
          
          {/* Order Tracking */}
          <Route path="/orders" element={<OrderTracking />} />
          <Route path="/orders/:orderNumber" element={<OrderTracking />} />
          
          {/* Shared Wishlist (public) */}
          <Route path="/wishlist/:shareCode" element={<SharedWishlist />} />
          
          {/* Contact Page */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/stockists" element={<StockistDeck />} />
          <Route path="/find-a-stockist" element={<FindAStockist />} />
          
          {/* Merch Shop */}
          <Route path="/merch" element={<MerchShop />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
            </Suspense>
            {/* Chat Widget for B2B users - visible site-wide */}
            <ChatWidget />
          </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
